import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Address,
  AddressInput,
  AddressValidationResultTypenames,
  ChannelType,
  SuggestedAddress,
} from '@varo/graphql-schema-types';
import useChangeAddress from 'features/myVaro/hooks/useChangeAddress';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import theme from 'shared/styles/theme';

interface AddressVerificationScreenProps {
  bottomSheetRef?: RefObject<BottomSheetModal | null> | null;
  setShowBottomSheet?: React.Dispatch<React.SetStateAction<boolean>>;
}

const getAddressVerificationTitle = (
  isSuggestedAddress: boolean,
  screenType: string | undefined,
  translatedText: (key: string) => string,
) => {
  if (isSuggestedAddress) {
    return translatedText('addressVerification.suggestedAddressTitle');
  }

  return translatedText(
    screenType === 'homeAddressScreen'
      ? 'addressVerification.homeAddressTitle'
      : 'addressVerification.mailingAddressTitle',
  );
};

const AddressVerificationScreen = ({
  bottomSheetRef,
  setShowBottomSheet,
}: AddressVerificationScreenProps) => {
  const {
    addressVerification,
    isIdenticalHomeAndMailingAddress,
    setIsCheckedHomeAndMailingAddress,
  } = useContext(MyVaroContext);
  const origAddress = addressVerification?.fullAddress?.originalAddress;
  const address = addressVerification?.fullAddress?.suggestedAddress;
  const addressTypename = addressVerification?.fullAddress
    ? addressVerification?.addressType
    : undefined;

  const isInvalidAddress =
    addressTypename === AddressValidationResultTypenames.InvalidAddress;

  const { isVloc, isLimitedAccess, fromConfirmAddress, screenType } =
    addressVerification ?? {};

  const isSuggestedAddress =
    addressTypename === AddressValidationResultTypenames.SuggestedAddress;

  const { loading, setUpdatedAddress, triggerUpdate } = useChangeAddress({
    channelType: ChannelType.Customer,
    isVloc: isVloc ?? false,
    isLimitedAccess: isLimitedAccess ?? false,
    fromConfirmAddress: fromConfirmAddress ?? false,
    screenType,
    setShowBottomSheet,
  });

  const onEditAddressHandler = () => {
    bottomSheetRef?.current?.dismiss();
    setShowBottomSheet?.(false);
  };
  const onAcceptHandler = () => {
    if (isIdenticalHomeAndMailingAddress) {
      // Validation already passed — trigger the mutation directly with the address
      setIsCheckedHomeAndMailingAddress?.(false);
      triggerUpdate(origAddress as AddressInput);
    } else {
      setUpdatedAddress(address as AddressInput);
    }
  };

  const { analyticsEvents } = useAnalyticsConfiguration();

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'personalDetails',
  });

  const formatAddress = (
    addr: Address | AddressInput | null | undefined,
    includeCountry: boolean = true,
  ): string => {
    if (!addr) {
      return '';
    }

    const line1 = addr.addressLine1 ?? '';
    const line2 = addr.addressLine2 ? `, ${addr.addressLine2}` : '';
    const city = addr.city ?? '';
    const state = addr.state ?? '';
    const zip = addr.zip ?? '';
    const country = includeCountry && addr.country ? ` ${addr.country}` : '';

    return `${line1}${line2}\n${city}, ${state} ${zip}${country}`;
  };

  const fullAddress = addressVerification?.fullAddress;

  const errorDescription = useMemo(() => {
    const fields = (fullAddress as SuggestedAddress)?.fields;
    if (fields?.length) {
      return fields
        .map((f) => f.message)
        .filter(Boolean)
        .join('; ');
    }
    return addressTypename ?? '';
  }, [fullAddress, addressTypename]);

  const isHomeAddressScreen = screenType === 'homeAddressScreen';

  const screenAnalyticsKey = isHomeAddressScreen
    ? '/myvaro/personal-details/change-address/home-address-confirmation'
    : '/myvaro/personal-details/change-mailing-address/mailing-address-confirmation';

  const hasLoggedScreenEvent = useRef(false);

  useEffect(() => {
    if (hasLoggedScreenEvent.current) {
      return;
    }
    hasLoggedScreenEvent.current = true;
    void analyticsEvents.logScreenEventFromConfig(screenAnalyticsKey, {
      error_description: errorDescription,
    });
  }, [analyticsEvents, screenAnalyticsKey, errorDescription]);

  const originalAddress = formatAddress(origAddress, true);
  const suggestedAddress = formatAddress(address, true);

  const homeAddress =
    screenType === 'homeAddressScreen'
      ? formatAddress(origAddress, false)
      : formatAddress(address, false);

  const mailingAddress =
    screenType === 'homeAddressScreen'
      ? formatAddress(address, false)
      : formatAddress(origAddress, false);

  return (
    <BottomSheet
      ref={bottomSheetRef}
      contentContainerStyle={styles.bottomSheetContainer}
      onDismiss={() => {
        setShowBottomSheet?.(false);
      }}
    >
      <View style={[styles.mediumSpacing, styles.container]}>
        {isIdenticalHomeAndMailingAddress ? (
          <TitleLgText style={styles.bottomGap}>
            {translatedText('addressVerification.identicalAddressTitle')}
          </TitleLgText>
        ) : (
          <TitleLgText style={styles.bottomGap}>
            {getAddressVerificationTitle(
              isSuggestedAddress,
              screenType,
              translatedText,
            )}
          </TitleLgText>
        )}

        {!isIdenticalHomeAndMailingAddress ? (
          <BodyBaseText style={styles.bottomGap}>
            {fullAddress?.message}
          </BodyBaseText>
        ) : null}

        {isIdenticalHomeAndMailingAddress ? (
          <>
            <BodyBaseText style={styles.bottomGap}>
              {translatedText('addressVerification.identicalAddressMessage')}
            </BodyBaseText>
            <BodyBaseBoldText>
              {translatedText('addressVerification.homeAddress')}
            </BodyBaseBoldText>
            <BodyBaseText style={styles.bottomGap}>{homeAddress}</BodyBaseText>
            <BodyBaseBoldText>
              {translatedText('addressVerification.mailingAddress')}
            </BodyBaseBoldText>
            <BodyBaseText>{mailingAddress}</BodyBaseText>
          </>
        ) : null}

        {!isIdenticalHomeAndMailingAddress && fullAddress?.originalAddress ? (
          <>
            <BodyBaseBoldText>
              {translatedText('addressVerification.youEntered')}
            </BodyBaseBoldText>
            <BodyBaseText style={!isInvalidAddress && styles.bottomGap}>
              {originalAddress}
            </BodyBaseText>
          </>
        ) : null}

        {!isIdenticalHomeAndMailingAddress && fullAddress?.suggestedAddress ? (
          <>
            <BodyBaseBoldText>
              {translatedText('addressVerification.suggestedAddress')}
            </BodyBaseBoldText>
            <BodyBaseText>{suggestedAddress}</BodyBaseText>
          </>
        ) : null}
      </View>
      <View style={[styles.screen, styles.buttonSpacing]}>
        {!isIdenticalHomeAndMailingAddress ? (
          <>
            {!isInvalidAddress ? (
              <Button
                label={translatedText(
                  'addressVerification.buttons.useSuggestedAddress',
                )}
                type="PRIMARY"
                onPress={onAcceptHandler}
                isLoading={loading}
                analyticsId="acceptAddressClicked"
              />
            ) : null}
            {isSuggestedAddress ? (
              <Button
                label={translatedText(
                  'addressVerification.buttons.editOriginal',
                )}
                type="SECONDARY"
                onPress={onEditAddressHandler}
                analyticsId="editAddressClicked"
              />
            ) : (
              <Button
                label={translatedText(
                  'addressVerification.buttons.editAddress',
                )}
                type="PRIMARY"
                onPress={onEditAddressHandler}
                analyticsId="editAddressClicked"
              />
            )}
          </>
        ) : (
          <>
            <Button
              label={translatedText('addressVerification.buttons.continue')}
              type="PRIMARY"
              onPress={onAcceptHandler}
            />
            <Button
              label={translatedText('addressVerification.buttons.editAddress')}
              type="SECONDARY"
              onPress={onEditAddressHandler}
            />
          </>
        )}
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mediumSpacing: {
    padding: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
  },
  bottomSheetContainer: {
    paddingTop: 0,
  },
  bottomGap: {
    paddingBottom: theme.spacing.m,
  },
  buttonSpacing: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
});

export default AddressVerificationScreen;
